import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import TranslationProgressBanner from "../components/TranslationProgressBanner";
import { useAuth } from "../contexts/AuthContext";
import { Send, MessageCircle, BookOpen, AtSign, Eye, Check, X, Trash2, Paperclip, Undo2, FileText, Download, Users, ChevronLeft } from "lucide-react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import GroupDMSection from "../components/GroupDMSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 8000;

function formatBytes(b) {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} kB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function Meldinger() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("dm"); // dm | guestbook | mentions
  const [summary, setSummary] = useState({ dm_unread: 0, mention_pending: 0, guestbook_pending: 0, show_mention: true, show_guestbook: true });
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [pendingGb, setPendingGb] = useState([]);
  const [pendingMentions, setPendingMentions] = useState([]);
  const [acting, setActing] = useState(null);
  const [quota, setQuota] = useState({ used: 0, limit: null, over_limit: false, tier: "free" });
  const [overLimitMsg, setOverLimitMsg] = useState("");
  const [selectedConvs, setSelectedConvs] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedMsgs, setSelectedMsgs] = useState(() => new Set());
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{id, filename, content_type, size}]
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const targetUsername = searchParams.get("til") || searchParams.get("to");
  const context = searchParams.get("context") === "creator" ? "creator" : "private";
  const isCreatorInbox = context === "creator";
  // `via=creator` på URL betyr: neste melding du sender skal tagges som "via skaperside"
  // slik at den lander i mottakerens skaper-innboks. Brukes når fan klikker "Send melding"
  // fra `/skaper/{handle}`.
  const viaTag = searchParams.get("via") === "creator" ? "creator" : null;

  const loadSummary = () =>
    api.get("/inbox/summary").then((r) => setSummary(r.data)).catch(() => {});

  const loadConversations = () =>
    api.get(`/conversations?context=${context}`).then((r) => setConversations(r.data)).catch(() => {});

  const loadQuota = () =>
    api.get("/messages/quota").then((r) => setQuota(r.data)).catch(() => {});

  const loadPendingGb = () =>
    api.get("/inbox/guestbook").then((r) => setPendingGb(r.data)).catch(() => {});

  const loadPendingMentions = () =>
    api.get("/mentions?status=pending&limit=50").then((r) => setPendingMentions(r.data)).catch(() => {});

  const openConversation = async (convId) => {
    setOverLimitMsg("");
    try {
      const r = await api.get(`/conversations/${convId}/messages`);
      setActiveConv({
        id: r.data.conversation_id,
        other: r.data.other,
        messages: r.data.messages || [],
      });
      await api.post(`/conversations/${convId}/read`).catch(() => {});
      loadConversations();
      loadQuota();
    } catch (e) {
      // Innboks over grensen → backend returnerer 402 med strukturert detail
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "inbox_over_limit") {
        setActiveConv({
          id: convId,
          other: conversations.find((c) => c.id === convId)?.other || { username: "" },
          messages: [],
          locked: true,
        });
        setOverLimitMsg(detail.message);
      } else {
        setError(formatApiError(e));
      }
    }
  };

  const deleteConversation = async (convId) => {
    if (!window.confirm(t("dm.confirm_delete_conv"))) return;
    try {
      await api.delete(`/conversations/${convId}`);
      if (activeConv?.id === convId) {
        setActiveConv(null);
        setOverLimitMsg("");
      }
      await loadConversations();
      await loadQuota();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const deleteMessage = async (msgId) => {
    if (!window.confirm(t("dm.confirm_delete_msg"))) return;
    try {
      await api.delete(`/messages/${msgId}`);
      if (activeConv) {
        setActiveConv({
          ...activeConv,
          messages: activeConv.messages.filter((m) => m.id !== msgId),
        });
      }
      await loadQuota();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const toggleSelected = (convId) => {
    setSelectedConvs((prev) => {
      const next = new Set(prev);
      if (next.has(convId)) next.delete(convId); else next.add(convId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedConvs((prev) => {
      if (prev.size === conversations.length) return new Set();
      return new Set(conversations.map((c) => c.id));
    });
  };

  const bulkDeleteSelected = async () => {
    const totalConvs = selectedConvs.size;
    const totalMsgs = selectedMsgs.size;
    if (totalConvs === 0 && totalMsgs === 0) return;
    const parts = [];
    if (totalConvs) parts.push(`${totalConvs} ${totalConvs === 1 ? t("dm.conv_one") : t("dm.conv_other")}`);
    if (totalMsgs)  parts.push(`${totalMsgs} ${totalMsgs === 1 ? t("dm.msg_one") : t("dm.msg_other")}`);
    if (!window.confirm(t("dm.confirm_bulk_delete", { parts: parts.join(" + ") }))) return;
    setBulkDeleting(true);
    try {
      await Promise.all([
        ...Array.from(selectedConvs).map((id) => api.delete(`/conversations/${id}`)),
        ...Array.from(selectedMsgs).map((id) => api.delete(`/messages/${id}`)),
      ]);
      if (activeConv && selectedConvs.has(activeConv.id)) {
        setActiveConv(null);
        setOverLimitMsg("");
      } else if (activeConv && totalMsgs > 0) {
        setActiveConv({
          ...activeConv,
          messages: activeConv.messages.filter((m) => !selectedMsgs.has(m.id)),
        });
      }
      setSelectedConvs(new Set());
      setSelectedMsgs(new Set());
      await loadConversations();
      await loadQuota();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Initial load + reload når context endres
  useEffect(() => {
    if (!user || user === false) return;
    loadConversations();
    loadSummary();
    loadQuota();
    setActiveConv(null); // tøm valgt samtale ved kontekst-bytte
    setSelectedConvs(new Set());
    setSelectedMsgs(new Set());
  }, [user, context]);

  useEffect(() => {
    if (!user || user === false) return;
    if (tab === "guestbook") loadPendingGb();
    if (tab === "mentions") loadPendingMentions();
  }, [tab, user]);

  const approveGuestbook = async (id) => {
    setActing(id);
    try {
      await api.post(`/guestbook/${id}/visibility`); // pending er is_public=false → toggle gjør den public
      setPendingGb((prev) => prev.filter((x) => x.id !== id));
      loadSummary();
    } finally { setActing(null); }
  };
  const deleteGuestbook = async (id) => {
    if (!window.confirm(t("dm.confirm_delete_gb"))) return;
    setActing(id);
    try {
      await api.delete(`/guestbook/${id}`);
      setPendingGb((prev) => prev.filter((x) => x.id !== id));
      loadSummary();
    } finally { setActing(null); }
  };
  const decideMention = async (id, decision) => {
    setActing(id);
    try {
      await api.post(`/mentions/${id}/${decision}`);
      setPendingMentions((prev) => prev.filter((x) => x.id !== id));
      loadSummary();
    } finally { setActing(null); }
  };

  // Hvis URL har ?til=username, opprett "tom" samtale-stub
  useEffect(() => {
    if (!targetUsername || !user || user === false) return;
    const existing = conversations.find(
      (c) => c.other.username?.toLowerCase() === targetUsername.toLowerCase()
    );
    if (existing) {
      openConversation(existing.id);
      setSearchParams({}, { replace: true });
    } else {
      setActiveConv({
        id: null,
        other: { username: targetUsername },
        messages: [],
        pending: true,
      });
    }
    // eslint-disable-next-line
  }, [targetUsername, conversations.length]);

  // Poll aktiv samtale (pause når fane er skjult)
  useEffect(() => {
    if (!activeConv?.id) return;
    const t = setInterval(async () => {
      if (document.hidden) return;
      try {
        const r = await api.get(`/conversations/${activeConv.id}/messages`);
        setActiveConv((c) => c && { ...c, messages: r.data.messages || [] });
        await api.post(`/conversations/${activeConv.id}/read`).catch(() => {});
      } catch {}
    }, POLL_MS);
    return () => clearInterval(t);
  }, [activeConv?.id]);

  // Poll samtaler-liste (pause når fane er skjult)
  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden) return;
      loadConversations();
    }, POLL_MS);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll til bunn
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollTop = bottomRef.current.scrollHeight;
  }, [activeConv?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const trimmed = body.trim();
    if (!trimmed && pendingAttachments.length === 0) return;
    if (!activeConv?.other?.username) return;
    setSending(true);
    setError("");
    try {
      await api.post(`/conversations/${activeConv.other.username}/messages`, {
        body: trimmed,
        attachment_ids: pendingAttachments.map((a) => a.id),
        // via=creator når sender startet samtalen fra mottakerens skaperside (viaTag)
        // ELLER når avsender selv er en creator som svarer i sin skaper-innboks
        // (isCreatorInbox). Dette holder samtalen i skaper-konteksten på begge sider
        // sin separate innboks-visning.
        via: (viaTag === "creator" || isCreatorInbox) ? "creator" : undefined,
      });
      setBody("");
      setPendingAttachments([]);
      const r = await api.get(`/conversations?context=${context}`);
      setConversations(r.data);
      const conv = r.data.find(
        (c) => c.other.username?.toLowerCase() === activeConv.other.username.toLowerCase()
      );
      if (conv) await openConversation(conv.id);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSending(false);
    }
  };

  const toggleMsgSelected = (msgId) => {
    setSelectedMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId); else next.add(msgId);
      return next;
    });
  };

  const toggleSelectAllMsgs = () => {
    setSelectedMsgs((prev) => {
      const msgs = activeConv?.messages || [];
      if (prev.size === msgs.length) return new Set();
      return new Set(msgs.map((m) => m.id));
    });
  };

  const bulkDeleteMsgs = async () => {
    if (selectedMsgs.size === 0) return;
    const n = selectedMsgs.size;
    if (!window.confirm(n === 1 ? t("dm.confirm_delete_n_msgs_one", { count: n }) : t("dm.confirm_delete_n_msgs_other", { count: n }))) return;
    try {
      await Promise.all(Array.from(selectedMsgs).map((id) => api.delete(`/messages/${id}`)));
      if (activeConv) {
        setActiveConv({
          ...activeConv,
          messages: activeConv.messages.filter((m) => !selectedMsgs.has(m.id)),
        });
      }
      setSelectedMsgs(new Set());
      await loadQuota();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const recallMessage = async (msgId) => {
    if (!window.confirm(t("dm.confirm_recall"))) return;
    try {
      await api.post(`/messages/${msgId}/recall`);
      if (activeConv) {
        setActiveConv({
          ...activeConv,
          messages: activeConv.messages.map((m) =>
            m.id === msgId ? { ...m, is_recalled: true, body: "", attachments: [] } : m
          ),
        });
      }
      loadConversations();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingFile(true);
    setError("");
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/messages/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(r.data);
      }
      setPendingAttachments((prev) => [...prev, ...uploaded].slice(0, 5));
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (id) =>
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));

  // Nullstill melding-utvalg når man bytter samtale
  useEffect(() => { setSelectedMsgs(new Set()); }, [activeConv?.id]);

  if (user === false || user === null) {
    return (
      <Layout>
        <div className="font-fraunces italic text-[var(--cream-soft)]">
          <Link to="/login" className="text-[var(--magenta)]">{t("dm.login_required")}</Link> {t("dm.login_required_suffix")}
        </div>
      </Layout>
    );
  }

  return (
    <Layout withSidebar={false}>
      <TranslationProgressBanner />
      <div className="flex items-baseline gap-3 flex-wrap mb-2">
        <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight" data-testid="inbox-title">
          {isCreatorInbox ? t("dm.creator_inbox", "Skaper-innboks.") : t("dm.inbox", "Innboks.")}
        </h1>
        {user?.is_creator && (
          <div className="flex gap-1 ml-auto" data-testid="inbox-context-toggle">
            <button
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("context");
                next.delete("til");
                next.delete("via");
                setSearchParams(next);
              }}
              data-testid="inbox-tab-private"
              className={`px-3 py-1 text-[10px] font-meta tracking-[0.2em] border transition ${
                !isCreatorInbox
                  ? "bg-[var(--cream)] text-[var(--noir)] border-[var(--cream)]"
                  : "border-white/20 text-[var(--cream-soft)] hover:border-[var(--cream)]"
              }`}
            >
              {t("dm.tab_private")} {summary.dm_unread_private > 0 && <span className="ml-1 text-[var(--magenta)]">({summary.dm_unread_private})</span>}
            </button>
            <button
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set("context", "creator");
                next.delete("til");
                next.delete("via");
                setSearchParams(next);
              }}
              data-testid="inbox-tab-creator"
              className={`px-3 py-1 text-[10px] font-meta tracking-[0.2em] border transition ${
                isCreatorInbox
                  ? "bg-[var(--magenta)] text-white border-[var(--magenta)]"
                  : "border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)]"
              }`}
            >
              {t("dm.tab_creator")} {summary.dm_unread_creator > 0 && <span className="ml-1 text-white">({summary.dm_unread_creator})</span>}
            </button>
          </div>
        )}
      </div>
      {isCreatorInbox && (
        <p className="text-xs text-[var(--cream-soft)] mb-6" data-testid="creator-inbox-hint">
          {t("dm.creator_hint_prefix")} <span className="text-[var(--magenta)]">@{user?.creator_handle || user?.username}</span> {t("dm.creator_hint_suffix")}
        </p>
      )}
      {!isCreatorInbox && <div className="mb-6" />}

      {/* Faner */}
      <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto" data-testid="inbox-tabs">
        <TabButton k="dm" cur={tab} onClick={() => setTab("dm")} count={summary.dm_unread}
                   icon={<MessageCircle className="h-3.5 w-3.5" />} label={t("dm.tab_dm")} />
        <TabButton k="groups" cur={tab} onClick={() => setTab("groups")} count={0}
                   icon={<Users className="h-3.5 w-3.5" />} label={t("dm.tab_groups")} />
        {summary.show_guestbook && (
          <TabButton k="guestbook" cur={tab} onClick={() => setTab("guestbook")} count={summary.guestbook_pending}
                     icon={<BookOpen className="h-3.5 w-3.5" />} label={t("dm.tab_guestbook")} />
        )}
        {summary.show_mention && (
          <TabButton k="mentions" cur={tab} onClick={() => setTab("mentions")} count={summary.mention_pending}
                     icon={<AtSign className="h-3.5 w-3.5" />} label={t("dm.tab_mentions")} />
        )}
        <Link to="/innstillinger" className="ml-auto self-center font-meta text-[10px] text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="inbox-prefs-link">
          {t("dm.prefs")}
        </Link>
      </div>

      {tab === "groups" && (
        <GroupDMSection currentUser={user} />
      )}

      {tab === "guestbook" && (
        <div className="max-w-3xl" data-testid="inbox-guestbook">
          {pendingGb.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
              <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">{t("dm.no_pending_guestbook")}</div>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {pendingGb.map((g) => (
                <li key={g.id} className="py-6 flex items-start gap-4" data-testid={`gb-pending-${g.id}`}>
                  <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 bg-[var(--magenta)]/15 text-[var(--magenta)]">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-fraunces text-[var(--cream)] text-base leading-snug">
                      <Link to={`/profile/${g.actor.username}`} className="text-[var(--magenta)] hover:underline">@{g.actor.username}</Link> {t("dm.wrote_in_guestbook")}
                    </div>
                    <blockquote className="mt-2 border-l border-white/10 pl-3 font-manrope text-sm text-[var(--cream-soft)] whitespace-pre-wrap">{g.body}</blockquote>
                    <div className="font-meta mt-2">{formatRelative(g.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => approveGuestbook(g.id)} disabled={acting === g.id}
                            className="btn-magenta text-xs inline-flex items-center gap-1.5"
                            data-testid={`gb-approve-${g.id}`}>
                      <Eye className="h-3 w-3" /> {t("dm.show_to_others")}
                    </button>
                    <button onClick={() => deleteGuestbook(g.id)} disabled={acting === g.id}
                            className="btn-ghost text-xs inline-flex items-center gap-1.5"
                            data-testid={`gb-delete-${g.id}`}>
                      <Trash2 className="h-3 w-3" /> {t("dm.delete")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "mentions" && (
        <div className="max-w-3xl" data-testid="inbox-mentions">
          {pendingMentions.length === 0 ? (
            <div className="text-center py-16">
              <AtSign className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
              <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">{t("dm.no_pending_mentions")}</div>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {pendingMentions.map((m) => (
                <li key={m.id} className="py-6 flex items-start gap-4" data-testid={`mention-pending-${m.id}`}>
                  <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 bg-[var(--magenta)]/15 text-[var(--magenta)]">
                    <AtSign className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-fraunces text-[var(--cream)] text-base leading-snug">
                      <Link to={`/profile/${m.actor_username}`} className="text-[var(--magenta)] hover:underline">@{m.actor_username}</Link> {t("dm.wants_to_mention")}{" "}
                      <Link to={`/thread/${m.thread_id}`} className="italic hover:text-[var(--magenta)]">«{m.thread_title || t("dm.untitled")}»</Link>
                    </div>
                    {m.preview && (
                      <blockquote className="mt-2 border-l border-white/10 pl-3 font-manrope text-sm text-[var(--cream-soft)]">{m.preview}</blockquote>
                    )}
                    <div className="font-meta mt-2">{formatRelative(m.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => decideMention(m.id, "accept")} disabled={acting === m.id}
                            className="btn-magenta text-xs inline-flex items-center gap-1.5"
                            data-testid={`mention-accept-${m.id}`}>
                      <Check className="h-3 w-3" /> {t("dm.accept")}
                    </button>
                    <button onClick={() => decideMention(m.id, "reject")} disabled={acting === m.id}
                            className="btn-ghost text-xs inline-flex items-center gap-1.5"
                            data-testid={`mention-reject-${m.id}`}>
                      <X className="h-3 w-3" /> {t("dm.reject")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "dm" && (
      <div data-testid="meldinger-dm">
        {/* Kvote-banner – vises kun når det finnes en grense (free-bruker) */}
        {quota.limit !== null && (
          <div
            className={`mb-4 px-4 py-2.5 border rounded-sm flex items-center justify-between gap-3 ${
              quota.over_limit
                ? "border-[var(--magenta)]/60 bg-[var(--magenta)]/10 text-[var(--cream)]"
                : "border-white/10 bg-[var(--noir-elev)]/40 text-[var(--cream-soft)]"
            }`}
            data-testid="inbox-quota-banner"
          >
            <div className="font-meta text-xs">
              {t("dm.quota_label")} <span className="text-[var(--cream)] font-medium">{quota.used} / {quota.limit}</span> {t("dm.quota_messages")}
              {quota.over_limit && (
                <span className="ml-3 text-[var(--magenta)]">{t("dm.quota_over")}</span>
              )}
            </div>
            <Link to="/oppgrader" className="font-meta text-[10px] text-[var(--magenta)] hover:underline shrink-0">
              {t("dm.become_vip_arrow")}
            </Link>
          </div>
        )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-px bg-white/8 border border-white/8" data-testid="meldinger-layout" style={{ height: "calc(100vh - 320px)", minHeight: "520px" }}>
        {/* Samtaler — på mobil skjules listen når en samtale er valgt */}
        <aside className={`bg-[var(--noir)] flex-col h-full min-h-0 overflow-hidden ${activeConv ? "hidden lg:flex" : "flex"}`} data-testid="conversations-list">
          {conversations.length === 0 ? (
            <div className="p-6 font-fraunces italic text-[var(--cream-soft)]">
              {t("dm.no_conversations_yet")}
            </div>
          ) : (
            <>
              {/* Bulk-toolbar */}
              <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-white/10 bg-[var(--noir-elev)]/40 shrink-0">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="select-all">
                  <input
                    type="checkbox"
                    checked={selectedConvs.size > 0 && selectedConvs.size === conversations.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedConvs.size > 0 && selectedConvs.size < conversations.length;
                    }}
                    onChange={toggleSelectAll}
                    className="accent-[var(--magenta)] h-3.5 w-3.5"
                    data-testid="select-all-checkbox"
                  />
                  <span className="font-meta text-[10px]">{t("dm.select_all")}</span>
                </label>
                <button
                  onClick={bulkDeleteSelected}
                  disabled={(selectedConvs.size + selectedMsgs.size) === 0 || bulkDeleting}
                  className={`font-meta text-[10px] inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-sm transition-all ${
                    (selectedConvs.size + selectedMsgs.size) === 0
                      ? "border-white/25 bg-white/[0.04] text-[var(--cream)] cursor-not-allowed opacity-70"
                      : "border-[var(--magenta)] bg-[var(--magenta)]/15 text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-black"
                  }`}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3 w-3" />
                  {t("dm.delete_selected")}{(selectedConvs.size + selectedMsgs.size) > 0 ? ` (${selectedConvs.size + selectedMsgs.size})` : ""}
                </button>
              </div>

              {/* Scrollbar samtaleliste */}
              <ul className="flex-1 overflow-y-auto min-h-0">
                {conversations.map((c) => {
                  const checked = selectedConvs.has(c.id);
                  return (
                    <li key={c.id} className="border-b border-white/5">
                      <div
                        className={`flex items-center gap-2 px-3 py-3 transition-colors ${
                          activeConv?.id === c.id
                            ? "bg-[var(--noir-elev)]"
                            : "hover:bg-[var(--noir-elev)]/50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelected(c.id)}
                          className="accent-[var(--magenta)] h-3.5 w-3.5 shrink-0 cursor-pointer"
                          aria-label={t("dm.select_conv_with", { name: c.other.username })}
                          data-testid={`conv-select-${c.id}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={() => openConversation(c.id)}
                          data-testid={`conv-item-${c.id}`}
                          className="flex-1 min-w-0 text-left flex gap-3 items-center"
                        >
                          <Avatar className="h-10 w-10 rounded-sm border border-white/10 shrink-0">
                            <AvatarFallback className="bg-[var(--magenta)] text-black text-xs font-bold rounded-sm">
                              {(c.other.username || "?").slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-fraunces italic text-base text-[var(--cream)] truncate">
                                {c.other.username}
                              </span>
                              {c.unread > 0 && (
                                <span className="font-meta text-[10px] bg-[var(--magenta)] text-black px-1.5 py-0.5 shrink-0">
                                  {c.unread}
                                </span>
                              )}
                            </div>
                            <div className="font-meta text-[10px] truncate">
                              {c.last_message || "—"}
                            </div>
                          </div>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </aside>

        {/* Aktiv chat — på mobil skjules den når ingen er valgt */}
        <section className={`bg-[var(--noir)] flex-col h-full min-h-0 overflow-hidden ${activeConv ? "flex" : "hidden lg:flex"}`} data-testid="active-conversation">
          {!activeConv ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
              <MessageCircle className="h-10 w-10 text-[var(--cream-faint)] mb-3" />
              <div className="font-fraunces italic text-2xl text-[var(--cream-soft)]">
                {t("dm.choose_conversation")}
              </div>
            </div>
          ) : (
            <>
              {/* Top-toolbar – Velg alle for meldinger (samme høyde som conv-toolbar) */}
              {!activeConv.locked && (activeConv.messages || []).length > 0 ? (
                <div className="flex items-center px-6 py-2 border-b border-white/10 bg-[var(--noir-elev)]/40 shrink-0" data-testid="msg-toolbar">
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="msg-select-all">
                    <input
                      type="checkbox"
                      checked={selectedMsgs.size > 0 && selectedMsgs.size === (activeConv.messages || []).length}
                      ref={(el) => {
                        if (el) el.indeterminate = selectedMsgs.size > 0 && selectedMsgs.size < (activeConv.messages || []).length;
                      }}
                      onChange={toggleSelectAllMsgs}
                      className="accent-[var(--magenta)] h-3.5 w-3.5"
                      data-testid="msg-select-all-checkbox"
                    />
                    <span className="font-meta text-[10px]">{t("dm.select_all")}</span>
                  </label>
                </div>
              ) : (
                <div className="border-b border-white/10 py-2 shrink-0" />
              )}

              <header className="border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 shrink-0">
                <button
                  onClick={() => { setActiveConv(null); setOverLimitMsg(""); }}
                  className="lg:hidden p-1 text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                  aria-label="Tilbake til samtaler"
                  data-testid="back-to-conversations"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <Link
                  to={`/profile/${activeConv.other.username}`}
                  className="font-fraunces italic text-lg sm:text-xl text-[var(--cream)] hover:text-[var(--magenta)] mx-auto"
                  data-testid="active-conv-title"
                >
                  {activeConv.other.username}
                </Link>
                <span className="lg:hidden w-7" />{/* spacer for sentrering */}
              </header>

              {activeConv.locked ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12" data-testid="over-limit-overlay">
                  <div className="h-14 w-14 rounded-full bg-[var(--magenta)]/15 flex items-center justify-center mb-5">
                    <MessageCircle className="h-6 w-6 text-[var(--magenta)]" />
                  </div>
                  <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3 max-w-md">
                    {t("dm.inbox_full")}
                  </h3>
                  <p className="font-manrope text-sm text-[var(--cream-soft)] max-w-md leading-relaxed mb-6">
                    {overLimitMsg}
                  </p>
                  <Link to="/oppgrader" className="btn-magenta text-xs">
                    {t("dm.become_vip")}
                  </Link>
                </div>
              ) : (
              <>
              <div ref={bottomRef} className="flex-1 overflow-y-auto min-h-0 p-6 space-y-3" data-testid="messages-list">
                {activeConv.messages.length === 0 ? (
                  <div className="text-center font-fraunces italic text-[var(--cream-soft)] mt-10">
                    {t("dm.send_first")}
                  </div>
                ) : (
                  activeConv.messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    const checked = selectedMsgs.has(m.id);
                    const isRecalled = m.is_recalled;
                    return (
                      <div
                        key={m.id}
                        className={`flex group items-start gap-2 ${mine ? "justify-end" : "justify-start"}`}
                      >
                        {/* Checkbox foran melding (ALLTID synlig, foran både mine og motpartens) */}
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMsgSelected(m.id)}
                          className={`accent-[var(--magenta)] h-3.5 w-3.5 shrink-0 cursor-pointer mt-2 ${mine ? "order-2" : "order-0"}`}
                          aria-label={t("dm.select_message")}
                          data-testid={`msg-select-${m.id}`}
                        />

                        <div className={`max-w-[70%] ${mine ? "order-1" : "order-1"}`}>
                          <div
                            className={`px-4 py-2.5 ${
                              isRecalled
                                ? "bg-transparent border border-dashed border-white/20 text-[var(--cream-faint)] italic"
                                : mine
                                  ? "bg-[var(--magenta)]/12 border border-[var(--magenta)]/45 text-[var(--cream)] border-r-2"
                                  : "bg-[var(--noir-elev)] text-[var(--cream)] border border-white/10"
                            }`}
                          >
                            {isRecalled ? (
                              <p className="font-manrope text-sm">{t("dm.message_recalled")}</p>
                            ) : (
                              <>
                                {m.body && (
                                  <p className="font-manrope text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
                                )}
                                {(m.attachments || []).length > 0 && (
                                  <div className={`space-y-1.5 ${m.body ? "mt-2" : ""}`}>
                                    {m.attachments.map((a) => {
                                      const isImage = (a.content_type || "").startsWith("image/");
                                      const url = `${BACKEND}/api/messages/attachments/${a.id}/raw`;
                                      return isImage ? (
                                        <a key={a.id} href={url} target="_blank" rel="noreferrer" className="block" data-testid={`attach-img-${a.id}`}>
                                          <img src={url} alt={a.filename} className="max-h-48 rounded-sm border border-black/10" />
                                        </a>
                                      ) : (
                                        <a key={a.id} href={url} target="_blank" rel="noreferrer"
                                           className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-white/15 hover:bg-white/5"
                                           data-testid={`attach-file-${a.id}`}>
                                          <FileText className="h-4 w-4 shrink-0" />
                                          <span className="font-manrope text-xs truncate flex-1">{a.filename}</span>
                                          <span className="font-meta text-[9px] opacity-70">{formatBytes(a.size)}</span>
                                          <Download className="h-3 w-3 shrink-0 opacity-70" />
                                        </a>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            )}
                            <div className={`font-meta text-[9px] mt-1 ${mine && !isRecalled ? "text-[var(--magenta)]" : "text-[var(--cream-faint)]"}`}>
                              {formatRelative(m.created_at)}
                            </div>
                          </div>

                          {/* Hover-handlinger: Trekk tilbake (kun mine, ikke alt-trukket) + Slett enkelt */}
                          <div className={`hidden group-hover:flex gap-2 mt-1 ${mine ? "justify-end" : "justify-start"}`}>
                            {mine && !isRecalled && (
                              <button
                                onClick={() => recallMessage(m.id)}
                                className="font-meta text-[9px] inline-flex items-center gap-1 text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                                title={t("dm.recall_tooltip")}
                                data-testid={`msg-recall-${m.id}`}
                              >
                                <Undo2 className="h-2.5 w-2.5" /> {t("dm.recall")}
                              </button>
                            )}
                            <button
                              onClick={() => deleteMessage(m.id)}
                              className="font-meta text-[9px] inline-flex items-center gap-1 text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                              title={t("dm.delete_only_yours")}
                              data-testid={`msg-delete-${m.id}`}
                            >
                              <Trash2 className="h-2.5 w-2.5" /> {t("dm.delete")}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              </>
              )}

              {!activeConv.locked && (
              <form onSubmit={send} className="border-t border-white/10 p-4 space-y-2 shrink-0">
                {pendingAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="pending-attachments">
                    {pendingAttachments.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 px-2.5 py-1.5 border border-white/15 rounded-sm bg-[var(--noir-elev)]/50">
                        <FileText className="h-3.5 w-3.5 text-[var(--cream-soft)]" />
                        <span className="font-manrope text-xs text-[var(--cream)] max-w-[180px] truncate">{a.filename}</span>
                        <span className="font-meta text-[9px] text-[var(--cream-faint)]">{formatBytes(a.size)}</span>
                        <button type="button" onClick={() => removePendingAttachment(a.id)}
                                className="text-[var(--cream-faint)] hover:text-[var(--magenta)]" aria-label={t("dm.remove_attachment")}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile || pendingAttachments.length >= 5}
                    className="h-9 w-9 flex items-center justify-center border border-white/15 rounded-sm text-[var(--cream-soft)] hover:text-[var(--magenta)] hover:border-[var(--magenta)]/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                    title={pendingAttachments.length >= 5 ? t("dm.max_attachments") : t("dm.attach_file")}
                    data-testid="attach-button"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={onFilesPicked}
                    multiple
                    className="hidden"
                    data-testid="attach-file-input"
                  />
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    rows={2}
                    placeholder={t("dm.write_to", { name: activeConv.other.username })}
                    className="input-editorial resize-none flex-1"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || uploadingFile || (!body.trim() && pendingAttachments.length === 0)}
                    className="btn-magenta inline-flex items-center"
                    data-testid="message-send"
                  >
                    <Send className="h-3.5 w-3.5 mr-2" />
                    {sending ? t("dm.sending") : uploadingFile ? t("dm.uploading") : t("dm.send")}
                  </button>
                </div>
              </form>
              )}
              {error && (
                <div className="px-6 pb-3 font-medium text-sm text-[var(--cream)] border-l-2 border-[var(--magenta)] pl-3">
                  {error}
                </div>
              )}
            </>
          )}
        </section>
      </div>
      </div>
      )}
    </Layout>
  );
}

function TabButton({ k, cur, onClick, count, icon, label }) {
  const active = cur === k;
  return (
    <button
      onClick={onClick}
      data-testid={`inbox-tab-${k}`}
      className={`px-4 py-3 font-meta text-xs transition-colors border-b-2 -mb-px inline-flex items-center gap-2 ${
        active
          ? "border-[var(--magenta)] text-[var(--cream)]"
          : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"
      }`}
    >
      {icon}
      {label}
      {count > 0 && (
        <span className="font-meta text-[10px] bg-[var(--magenta)] text-black px-1.5 py-0.5 ml-1">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
}
